import { useState, useEffect } from 'react';
import {
  collection, query, where, getDocs,
  doc, updateDoc, getFirestore,
} from 'firebase/firestore';
import { getApp } from 'firebase/app';

const PORTAL_COLORS = {
  mp:'#0e9cc0', embedded:'#7F77DD', system:'#1D9E75',
  ops:'#BA7517', pe:'#378ADD', obc:'#639922',
  experimental:'#D85A30', finance:'#378ADD',
};

const MODES = [
  { id:'portal',     icon:'🏢', label:'All portal members',    desc:'Everyone in this portal can see this' },
  { id:'restricted', icon:'👥', label:'Specific members only', desc:'Choose exactly who can see this report' },
  { id:'private',    icon:'🔒', label:'Only me',               desc:'Draft — only you and admins can see this' },
];

export default function WithinPortalSharePanel({ post, postCol, portalId, currentUser, onClose }) {
  const db = getFirestore(getApp());
  const [portalMembers, setPortalMembers] = useState([]);
  const [selected,      setSelected]      = useState(post.restrictedToUids || []);
  const [mode,          setMode]          = useState(post.visibility || 'portal');
  const [saving,        setSaving]        = useState(false);

  const ac = PORTAL_COLORS[portalId] || '#0e9cc0';

  useEffect(() => {
    getDocs(query(
      collection(db, 'authorized_users'),
      where('role', '==', portalId),
      where('active', '==', true)
    )).then(snap => {
      setPortalMembers(
        snap.docs
          .map(d => ({ uid: d.id, ...d.data() }))
          .filter(m => m.uid !== currentUser?.uid)
      );
    }).catch(() => {});
  }, [portalId]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleMember = uid =>
    setSelected(prev => prev.includes(uid) ? prev.filter(id => id !== uid) : [...prev, uid]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateDoc(doc(db, postCol, post.id), {
        visibility:       mode,
        restrictedToUids: mode === 'restricted' ? selected : [],
      });
      onClose();
    } catch (err) {
      console.error('[WithinPortalShare] save error:', err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.55)', zIndex:500, display:'flex', alignItems:'center', justifyContent:'center' }}
      onClick={e => e.target === e.currentTarget && onClose()}
    >
      <div style={{ width:420, background:'var(--smc-panel)', border:`0.5px solid ${ac}40`, borderRadius:14, overflow:'hidden', maxHeight:'88vh', display:'flex', flexDirection:'column' }}>

        {/* Header */}
        <div style={{ padding:'14px 18px', borderBottom:'0.5px solid var(--smc-bd)', display:'flex', alignItems:'center', justifyContent:'space-between', flexShrink:0 }}>
          <div>
            <div style={{ fontSize:14, fontWeight:600, color:'var(--smc-tx)' }}>Share within portal</div>
            <div style={{ fontSize:10, color:'var(--smc-tx3)', marginTop:2 }}>Control who in your portal can see this report</div>
          </div>
          <button onClick={onClose} style={{ background:'none', border:'none', cursor:'pointer', color:'var(--smc-tx3)', fontSize:16 }}>✕</button>
        </div>

        <div style={{ flex:1, overflowY:'auto', padding:'14px 18px' }}>
          {/* Visibility mode */}
          <div style={{ fontSize:9, fontFamily:"'Share Tech Mono',monospace", color:'var(--smc-tx3)', letterSpacing:'0.1em', marginBottom:8 }}>
            WHO CAN SEE THIS REPORT
          </div>
          {MODES.map(m => (
            <div key={m.id} onClick={() => setMode(m.id)} style={{
              display:'flex', alignItems:'center', gap:10, padding:'9px 12px', borderRadius:8,
              cursor:'pointer', marginBottom:5, transition:'all .12s',
              border:`0.5px solid ${mode === m.id ? ac + '50' : 'var(--smc-bd)'}`,
              background: mode === m.id ? ac + '10' : 'transparent',
            }}>
              <div style={{ fontSize:18 }}>{m.icon}</div>
              <div style={{ flex:1 }}>
                <div style={{ fontSize:12, fontWeight: mode === m.id ? 500 : 400, color: mode === m.id ? ac : 'var(--smc-tx)' }}>{m.label}</div>
                <div style={{ fontSize:10, color:'var(--smc-tx3)' }}>{m.desc}</div>
              </div>
              <div style={{ width:16, height:16, borderRadius:'50%', flexShrink:0, display:'flex', alignItems:'center', justifyContent:'center', border:`1.5px solid ${mode === m.id ? ac : 'var(--smc-bd)'}`, background: mode === m.id ? ac : 'transparent' }}>
                {mode === m.id && <div style={{ width:6, height:6, borderRadius:'50%', background:'#fff' }} />}
              </div>
            </div>
          ))}

          {/* Member selector */}
          {mode === 'restricted' && (
            <div style={{ marginTop:14 }}>
              <div style={{ fontSize:9, fontFamily:"'Share Tech Mono',monospace", color:'var(--smc-tx3)', letterSpacing:'0.1em', marginBottom:8 }}>
                SELECT MEMBERS ({selected.length} selected)
              </div>
              {portalMembers.length === 0 ? (
                <div style={{ fontSize:11, color:'var(--smc-tx3)', padding:'8px 0' }}>No other members in this portal yet.</div>
              ) : (
                <div style={{ maxHeight:200, overflowY:'auto', border:'0.5px solid var(--smc-bd)', borderRadius:8 }}>
                  {portalMembers.map((member, idx) => {
                    const isSel = selected.includes(member.uid);
                    return (
                      <div key={member.uid} onClick={() => toggleMember(member.uid)} style={{
                        display:'flex', alignItems:'center', gap:9, padding:'9px 12px', cursor:'pointer',
                        borderBottom: idx < portalMembers.length - 1 ? '0.5px solid var(--smc-bd)' : 'none',
                        background: isSel ? ac + '08' : 'transparent', transition:'background .12s',
                      }}>
                        <div style={{ width:28, height:28, borderRadius:'50%', background: ac + '20', color:ac, fontSize:10, fontWeight:600, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                          {(member.name||'?').substring(0,2).toUpperCase()}
                        </div>
                        <div style={{ flex:1 }}>
                          <div style={{ fontSize:12, fontWeight:500, color:'var(--smc-tx)' }}>{member.name}</div>
                          <div style={{ fontSize:10, color:'var(--smc-tx3)' }}>{member.role}</div>
                        </div>
                        <div style={{ width:18, height:18, borderRadius:5, display:'flex', alignItems:'center', justifyContent:'center', fontSize:10, color:'#fff', flexShrink:0, transition:'all .12s', border:`1.5px solid ${isSel ? '#1D9E75' : 'var(--smc-bd)'}`, background: isSel ? '#1D9E75' : 'transparent' }}>
                          {isSel ? '✓' : ''}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
              <div style={{ marginTop:6, fontSize:10, color:'var(--smc-tx3)' }}>
                You and all admins can always see this report.
              </div>
            </div>
          )}

          {/* External shared indicator */}
          {post.externalShared && (
            <div style={{ marginTop:12, padding:'8px 12px', borderRadius:7, background:'rgba(127,119,221,0.10)', border:'0.5px solid rgba(127,119,221,0.25)', fontSize:11, color:'#7F77DD' }}>
              🔗 Shared externally by {post.sharedExternallyBy}
            </div>
          )}

          {/* Actions */}
          <div style={{ display:'flex', gap:8, justifyContent:'flex-end', marginTop:16 }}>
            <button onClick={onClose} style={{ padding:'8px 16px', borderRadius:7, background:'transparent', border:'0.5px solid var(--smc-bd)', color:'var(--smc-tx2)', fontSize:12, cursor:'pointer' }}>
              Cancel
            </button>
            <button onClick={handleSave} disabled={saving} style={{ padding:'8px 18px', borderRadius:7, background:ac, color:'#060b18', border:'none', fontSize:12, fontWeight:600, cursor: saving ? 'wait' : 'pointer', opacity: saving ? 0.7 : 1 }}>
              {saving ? 'Saving...' : 'Save settings'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
